/**
 * Assessment type constants shared across AssessmentForm, Dashboard, Results, and GrowthChart.
 *
 * testCategory groups related testTypes for reporting and filtering:
 *   letter_knowledge       — uppercase, lowercase, sounds, plus the two
 *                            optional vowel tests long_vowels / short_vowels
 *                            (v3.73.0; items in vowelTests.js)
 *   phonological_awareness — nonsense_word (NWF) and the five pa_* screener
 *                            steps. (The benchmark first_sound test was
 *                            retired in v3.67.0 — the testType id lives on
 *                            only in legacy seeded docs, which every surface
 *                            filters out.)
 *   beginning_of_year      — boy_shapes, boy_numbers, boy_colors (once-per-
 *                            school-year readiness checks; items in boyItems.js)
 */

// 25 CVC nonsense words for Nonsense Word Fluency (Pre-K / Kindergarten appropriate).
// Independently authored — not derived from any proprietary stimulus set.
export const NWF_WORDS = [
  'baf', 'tig', 'mup', 'vod', 'wej',
  'pib', 'dak', 'lun', 'fot', 'zel',
  'gup', 'jev', 'sib', 'tep', 'nob',
  'jud', 'kaf', 'niv', 'beg', 'wul',
  'poc', 'yat', 'dib', 'feg', 'miv',
];

export const TEST_CATEGORY_MAP = {
  uppercase:     'letter_knowledge',
  lowercase:     'letter_knowledge',
  sounds:        'letter_knowledge',
  // Optional Letter/Sound vowel tests (v3.73.0) — 5 items each, same doc
  // shape as the three letter tests; bank in src/constants/vowelTests.js.
  long_vowels:   'letter_knowledge',
  short_vowels:  'letter_knowledge',
  nonsense_word: 'phonological_awareness',
  // 4K PA Screener steps (v3.57.0). pa_onset_fluency is deliberately NOT
  // the retired benchmark first_sound id: same skill, different instrument
  // (5 untimed items scored 0-5 with bands, vs that 25-word timed fluency
  // measure scored 0-25).
  pa_rhyme_recognition:   'phonological_awareness',
  pa_rhyme_production:    'phonological_awareness',
  pa_blending_syllables:  'phonological_awareness',
  pa_segmenting_syllables:'phonological_awareness',
  pa_onset_fluency:       'phonological_awareness',
  // Phonological Awareness (4K) group (v3.70.0) — six STANDARD-pattern
  // assessments (Letter/Sound pattern: fixed form, 0-5, quarter-stamped),
  // NOT screener steps. `pa4k_` prefix keeps them disjoint from the
  // screener's pa_* ids; bank in src/constants/pa4kItems.js.
  pa4k_word_awareness:    'phonological_awareness',
  pa4k_rhyme_recognition: 'phonological_awareness',
  pa4k_alliteration:      'phonological_awareness',
  pa4k_syllables:         'phonological_awareness',
  pa4k_onset_rime:        'phonological_awareness',
  pa4k_beginning_sound:   'phonological_awareness',
  boy_shapes:    'beginning_of_year',
  boy_numbers:   'beginning_of_year',
  boy_colors:    'beginning_of_year',
  boy_pencil_grip:  'beginning_of_year',
  boy_name_writing: 'beginning_of_year',
  boy_handedness:   'beginning_of_year',
  boy_notes:        'beginning_of_year',
};

export const TEST_TYPE_LABELS = {
  uppercase:     'Uppercase Letters',
  lowercase:     'Lowercase Letters',
  sounds:        'Letter Sounds',
  long_vowels:   'Long Vowels',
  short_vowels:  'Short Vowels',
  nonsense_word: 'Nonsense Word Fluency',
  pa_rhyme_recognition:   'Rhyme Recognition',
  pa_rhyme_production:    'Rhyme Production',
  pa_blending_syllables:  'Blending Syllables',
  pa_segmenting_syllables:'Segmenting Syllables',
  // Display label: the five-step container is named "Phonemic Awareness"
  // (v3.67.0; Elmbrook's district form title), so step 5 keeps the form's
  // subtitle for the skill. testType unchanged — display string only.
  pa_onset_fluency:       'Initial Sound Isolation',
  // Phonological Awareness (4K) group. "Rhyme Recognition" deliberately
  // duplicates the pa_rhyme_recognition label string (Kemal-approved
  // 2026-08-31): distinct ids, and each label renders only inside its own
  // section's scoped surfaces.
  pa4k_word_awareness:    'Word Awareness',
  pa4k_rhyme_recognition: 'Rhyme Recognition',
  pa4k_alliteration:      'Alliteration',
  pa4k_syllables:         'Syllable Blending & Counting',
  pa4k_onset_rime:        'Onset-Rime Matching',
  pa4k_beginning_sound:   'Beginning Sound Isolation',
  boy_shapes:    'Geometric Shapes',
  boy_numbers:   'Numbers',
  boy_colors:    'Colors',
  boy_pencil_grip:  'Pencil Grip',
  boy_name_writing: 'Name Writing',
  boy_handedness:   'Dexterity',
  boy_notes:        'Notes',
};

// Ordered list used for sorting (relative order only — the numbers are
// ordinals, nothing persists them). The vowel tests sort directly after
// Sounds (v3.73.0: Long Vowels, then Short Vowels), so every later ordinal
// shifted by two; the relative order of everything else is unchanged.
export const TEST_TYPE_ORDER = {
  uppercase:     1,
  lowercase:     2,
  sounds:        3,
  long_vowels:   4,
  short_vowels:  5,
  nonsense_word: 7,
  boy_shapes:    8,
  boy_numbers:   9,
  boy_colors:    10,
  boy_pencil_grip:  11,
  boy_name_writing: 12,
  boy_handedness:   13,
  boy_notes:        14,
  // Screener steps sort in administration order, after the standalone
  // screeners so existing report ordering is undisturbed.
  pa_rhyme_recognition:   15,
  pa_rhyme_production:    16,
  pa_blending_syllables:  17,
  pa_segmenting_syllables:18,
  pa_onset_fluency:       19,
  // Phonological Awareness (4K) group, in administration order, after the
  // screener steps.
  pa4k_word_awareness:    20,
  pa4k_rhyme_recognition: 21,
  pa4k_alliteration:      22,
  pa4k_syllables:         23,
  pa4k_onset_rime:        24,
  pa4k_beginning_sound:   25,
};

// ─── Nonsense Word Fluency (benchmark screener) presentation settings ────────
//
// Configured per screener test under profile.screenerSettings, e.g.
//   screenerSettings: {
//     nonsense_word: { wordOrder, letterCase, useCustomList, customWords },
//   }
// Mirrors the CVC/HFW options pattern. Defaults preserve today's behavior
// (built-in 25-word set, fixed list order, words shown as authored).
// A stored `screenerSettings.first_sound` sub-map may still exist on older
// profiles (the test was retired in v3.67.0) — it is never read and never
// overwritten: the settings save touches only the nonsense_word key.

// Which tests expose the "customise the word list" settings UI.
//
// The 4K PA Screener's pa_* types are DELIBERATELY ABSENT and must stay
// that way. Two reasons, both hard requirements:
//   1. Parallel forms only mean something if every student gets the same
//      matched items. A teacher-substituted list destroys September-to-May
//      comparability and silently invalidates the band.
//   2. All 75 screener items are ours by construction and documented as
//      such in docs/assessments/ITEM_PROVENANCE.md. A free-text override
//      is precisely the path by which third-party published items could be
//      pasted into the product.
// Enforced by a test in src/constants/paScreenerItems.test.js.
export const SCREENER_TESTS = ['nonsense_word'];
export const SCREENER_WORD_ORDERS = ['fixed', 'random'];
export const SCREENER_LETTER_CASES = ['asIs', 'lowercase', 'uppercase'];

const SCREENER_BUILTIN = {
  nonsense_word: NWF_WORDS,
};

// A custom screener word: letters only, 2–8 chars (NWF stimuli are short
// CVC-style nonsense words).
export function isValidScreenerWord(word) {
  if (typeof word !== 'string') return false;
  return /^[a-zA-Z]{2,8}$/.test(word.trim());
}

export function applyScreenerCase(word, letterCase) {
  if (letterCase === 'uppercase') return String(word).toUpperCase();
  if (letterCase === 'lowercase') return String(word).toLowerCase();
  return word;
}

function shuffleWords(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Presentation-ready word list for one screener test, honoring that test's
// screenerSettings entry. Letter case is applied at display time.
export function getScreenerWords(testType, screenerSettings) {
  // Hard fence, not just convention: only the benchmark screeners may be
  // customised. Without this, a settings object naming a pa_* type would
  // hand back a teacher-supplied word list for the 4K PA Screener —
  // breaking form comparability and opening the third-party-items path
  // that SCREENER_TESTS documents. Tested in paScreenerItems.test.js.
  if (!SCREENER_TESTS.includes(testType)) return [];
  const base = SCREENER_BUILTIN[testType] || [];
  const s = (screenerSettings && screenerSettings[testType]) || {};
  const words = (s.useCustomList && Array.isArray(s.customWords) && s.customWords.length > 0)
    ? s.customWords.filter(isValidScreenerWord)
    : base;
  return s.wordOrder === 'random' ? shuffleWords(words) : [...words];
}

export const DEFAULT_SCREENER_TEST_SETTINGS = {
  wordOrder: 'fixed',
  letterCase: 'asIs',
  useCustomList: false,
  customWords: [],
};
